import { motion } from 'framer-motion'
import { assetPath } from '@/utils/assetPath'
import { forwardRef } from 'react'
import { Sparkles } from 'lucide-react'
import { classNames } from '@/utils/classNames'
// import GrainyGradient from './GrainyGradient'
// import { isWebGLAvailable } from '@/utils/webglDetection'
// import { CanvasErrorBoundary } from './CanvasErrorBoundary'

// WebGL-related interface (commented out for future use)
// interface RippleInterface {
//     id: number
//     x: number
//     y: number
//     startTime: number
// }

interface TryOnButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'short' | 'long'
    showIcon?: boolean
    icon?: React.ReactNode
    className?: string
}

export const TryOnButton = forwardRef<HTMLButtonElement, TryOnButtonProps>(
    (
        {
            variant = 'short',
            showIcon = true,
            icon,
            onClick,
            disabled = false,
            className,
            children,
            ...props
        },
        ref
    ) => {
        // WebGL-related state and refs (commented out for future use)
        // const [ripples, setRipples] = useState<RippleInterface[]>([])
        // const [currentTime, setCurrentTime] = useState(0)
        // const [webGLAvailable, setWebGLAvailable] = useState(false)
        // const [webGLChecked, setWebGLChecked] = useState(false)
        // const containerRef = useRef<HTMLDivElement>(null)
        // const rippleIdRef = useRef(0)

        // Check WebGL availability on mount (commented out for future use)
        // useEffect(() => {
        //     const available = isWebGLAvailable()
        //     setWebGLAvailable(available)
        //     setWebGLChecked(true)
        // }, [])

        // const handleTimeUpdate = (time: number) => {
        //     setCurrentTime(time)
        // }

        const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
            // Ripple creation logic (commented out for future use)
            // Only add ripples if WebGL is available (ripples require shaders)
            // if (webGLAvailable && containerRef.current) {
            //     const rect = containerRef.current.getBoundingClientRect()
            //     const x = e.clientX - rect.left
            //     const y = e.clientY - rect.top

            //     const newRipple: RippleInterface = {
            //         id: rippleIdRef.current++,
            //         x,
            //         y,
            //         startTime: currentTime,
            //     }

            //     setRipples((prev) => [...prev, newRipple])

            //     setTimeout(() => {
            //         setRipples((prev) =>
            //             prev.filter((ripple) => ripple.id !== newRipple.id)
            //         )
            //     }, 3000)
            // }

            if (onClick) {
                onClick(e)
            }
        }

        const getText = () => {
            if (children) return children
            return variant === 'long' ? 'Try on me' : 'Try on'
        }

        const {
            onDrag,
            onDragEnd,
            onDragStart,
            onAnimationStart,
            onAnimationEnd,
            onAnimationIteration,
            ...restProps
        } = props

        // CSS gradient fallback matching the shader colors (commented out for future use)
        // const gradientFallbackStyle: React.CSSProperties = {
        //     background: 'linear-gradient(to right, #FF3264 0%, #FAC8E1 25%, #FAB615 50%, #FF2828 75%, #8C0F0F 100%)',
        //     filter: 'drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.08)) drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.06))',
        // }

        // Base button style
        const baseButtonStyle: React.CSSProperties = {
            filter: 'drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.08)) drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.06))',
        }

        return (
            <motion.button
                ref={ref}
                type="button"
                onClick={handleClick}
                disabled={disabled}
                className={classNames(
                    'relative flex items-center justify-center gap-2 overflow-hidden rounded-2xl border-none shadow-lg transition-all',
                    disabled
                        ? 'cursor-not-allowed opacity-50'
                        : 'cursor-pointer',
                    className
                )}
                style={baseButtonStyle}
                whileHover={!disabled ? { scale: 1.02 } : undefined}
                transition={{
                    type: 'spring',
                    stiffness: 300,
                    damping: 30,
                    mass: 0.8,
                }}
                {...(restProps as any)}
            >
                {/* WebGL canvas rendering (commented out for future use) */}
                {/* {webGLAvailable ? (
                    <div
                        ref={containerRef}
                        className="pointer-events-none absolute inset-0"
                    >
                        <CanvasErrorBoundary
                            camera={{ position: [0, 0, 1] }}
                            gl={{ preserveDrawingBuffer: true }}
                            style={{ pointerEvents: 'none' }}
                            onError={() => {
                                setWebGLAvailable(false)
                            }}
                        >
                            <GrainyGradient
                                ripples={ripples}
                                onTimeUpdate={handleTimeUpdate}
                            />
                        </CanvasErrorBoundary>
                    </div>
                ) : null} */}

                {/* Image background */}
                <img
                    src={assetPath('images/try-on-button.jpg')}
                    alt=""
                    className="pointer-events-none absolute inset-0 h-full w-full object-cover"
                />

                <span className="relative font-semibold text-white select-none">
                    {getText()}
                </span>
                {showIcon &&
                    (icon || (
                        <Sparkles className="relative h-4 w-4 text-white" />
                    ))}
            </motion.button>
        )
    }
)

TryOnButton.displayName = 'TryOnButton'
